import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { OverrideProperties } from 'type-fest';
import Account from 'types/temporaryManualTypes/Account.interface';

import {
  type SelectProps,
  Select,
  CreatableSelect,
  Box,
  HStack,
  SelectComponents,
  Mark,
  useDisclosure,
} from '@frontend/components';

import AccountDrawer from 'modules/common/drawers/accountDrawer/AccountDrawer';
import { MODES } from 'modules/common/drawers/accountDrawer/data/constants';

import { useDebounce } from 'utils/hooks';

import { accountSearch, fetchAccount, receiveAccount } from './data/actions';
import { CREATE_NEW_ACCOUNT, UNASSIGNED_VALUE } from './data/constants';
import {
  getCachedAccounts,
  getIsSearching,
  getSearchResults,
} from './data/reducer';

// Safe HTML parser component that extracts highlighted text
const SafeHighlightedText = ({
  htmlString,
  fallbackText,
}: {
  htmlString?: string[];
  fallbackText: string;
}) => {
  if (!htmlString) {
    return <>{fallbackText}</>;
  }

  // Join the string array into a single string, then split by <em> tags
  const parts = htmlString.join('').split(/(<em>.*?<\/em>)/g);

  return (
    <>
      {parts.map((part: string, index: number) => {
        if (part.startsWith('<em>') && part.endsWith('</em>')) {
          // Extract text from <em> tags and wrap with Mark component
          const highlightedText = part.replace(/<\/?em>/g, '');
          return (
            // eslint-disable-next-line react/no-array-index-key
            <Mark key={index}>{highlightedText}</Mark>
          );
        } else {
          // Regular text
          // eslint-disable-next-line react/no-array-index-key
          return <span key={index}>{part}</span>;
        }
      })}
    </>
  );
};

const CustomOption = ({ innerProps, children, data, ...restProps }) => (
  <SelectComponents.Option
    {...restProps}
    data={data}
    innerProps={{ ...innerProps, flexWrap: 'wrap' }}
  >
    {data.value === UNASSIGNED_VALUE ? (
      data.label
    ) : !data.account ? (
      data.label
    ) : (
      <>
        {data.account.data.name && (
          <Box as="strong" width="100%" fontWeight="heavy">
            <SafeHighlightedText
              htmlString={data.account.highlight?.name}
              fallbackText={data.account.data.name ?? ''}
            />
          </Box>
        )}
        {data.account.data.erpCode && (
          <Box as="span" width="100%" color="feature.secondary">
            ERP Code:&nbsp;
            <SafeHighlightedText
              htmlString={data.account.highlight?.erpCode}
              fallbackText={data.account.data.erpCode ?? ''}
            />
          </Box>
        )}
        <HStack divider={<>&nbsp;|&nbsp;</>} color="feature.secondary">
          {data.account.data.phone && (
            <Box as="span">
              <SafeHighlightedText
                htmlString={data.account.highlight?.phone}
                fallbackText={data.account.data.phone ?? ''}
              />
            </Box>
          )}
          {data.account.data.url && (
            <Box as="span">
              <SafeHighlightedText
                htmlString={data.account.highlight?.url}
                fallbackText={data.account.data.url ?? ''}
              />
            </Box>
          )}
        </HStack>
      </>
    )}
  </SelectComponents.Option>
);

export type AccountSelectorProps = OverrideProperties<
  SelectProps,
  {
    allowUnassignedOption?: boolean;
    allowMixedUnassignedOptions?: boolean;
    onChange?: (selections: string | null) => void;
    searchFields?: string[];
    searchFilters?: any;
    value?: (number | string)[];
    textFeedback?: (text: string) => void;
    mode?: string;
    menuPortalDestinationRef?: any;
  }
>;

export const AccountSelector = forwardRef<'div', AccountSelectorProps>(
  (
    {
      mode,
      isMulti = false,
      allowUnassignedOption = false,
      allowMixedUnassignedOptions,
      onChange,
      placeholder: placeholderProp,
      value = [],
      textFeedback,
      components,
      ...restProps
    },
    ref,
  ) => {
    const dispatch = useDispatch();

    const accountDrawerDisclosure = useDisclosure();
    const cachedAccounts = useSelector(getCachedAccounts);
    const isSearching = useSelector(getIsSearching);
    const searchResults = useSelector(getSearchResults) as Array<{
      data: Account;
    }>;

    const fetchAccountRequest = useCallback(
      (...args) => dispatch(fetchAccount.request(...args)),
      [dispatch],
    );
    const receiveAccountRequest = useCallback(
      (...args) => dispatch(receiveAccount.request(...args)),
      [dispatch],
    );
    const accountSearchRequest = useCallback(
      (...args) => dispatch(accountSearch.request(...args)),
      [dispatch],
    );

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOptions, setSelectedOptions] = useState([]);
    const debouncedSearchTerm = useDebounce(searchTerm, 250);
    const debounceSearchCount = useRef(0);
    const accountsRequested = useRef({});

    /**
     * If there are selected values that have not been found from our current search,
     * we need to fetch them and map them to options.
     */
    const values = Array.isArray(value) ? value : [value];
    const missingSelectedOptions = values.filter(
      (value) =>
        !selectedOptions.some(
          (selectedOption) => selectedOption.value == value,
        ),
    );
    if (missingSelectedOptions.length) {
      // there are selected options that have not been found via search
      const optionsFromCache = [];
      missingSelectedOptions.forEach((missingOption) => {
        const cachedAccount = cachedAccounts.find(
          (account) => account.id == missingOption,
        );
        // map the cached account to resemble a search result
        if (cachedAccount) {
          optionsFromCache.push({
            value: cachedAccount.id,
            label: cachedAccount.name,
            account: { data: cachedAccount },
          });
        } else {
          // fetch the missing account from the backend
          if (!accountsRequested.current[missingOption]) {
            fetchAccountRequest({ accountId: missingOption });
            accountsRequested.current[missingOption] = true;
          }
        }

        if (optionsFromCache.length) {
          setSelectedOptions([...selectedOptions, ...optionsFromCache]);
        }
      });
    }

    let options = [
      ...selectedOptions,
      ...searchResults
        .sort((a, b) => a.data.name.localeCompare(b.data.name))
        .map((account) => {
          return {
            value: account?.data?.id ?? null,
            label: account?.data?.name ?? '',
            account: account,
          };
        }),
    ];

    if (allowUnassignedOption) {
      options.unshift({
        account: { data: { id: UNASSIGNED_VALUE } },
        value: UNASSIGNED_VALUE,
        label: 'Unassigned',
      });
    }

    options = options.filter(
      (option, index) =>
        options.findIndex((opt) => opt.value == option.value) == index,
    );

    const flatOptions = options.flatMap((option) => option.options ?? option);
    // initiate on load
    useEffect(() => {
      accountSearchRequest({
        search: '',
      });
    }, [accountSearchRequest]);

    useEffect(() => {
      // do not send update on initial load
      if (debounceSearchCount.current > 0) {
        accountSearchRequest({
          search: debouncedSearchTerm,
        });
      }
      debounceSearchCount.current++;
    }, [debouncedSearchTerm, accountSearchRequest]);

    function convertToValues(selections: any) {
      if (selections.value != null) {
        return [selections.value];
      }

      if (Array.isArray(selections)) {
        return selections.map((selection) => selection.value ?? selection);
      }
    }

    const handleChange = (selections: any) => {
      if (!selections) {
        onChange?.(null);
        return;
      }

      let nextSelections = convertToValues(selections);

      if (isMulti && allowUnassignedOption && !allowMixedUnassignedOptions) {
        if ((value || []).includes(UNASSIGNED_VALUE)) {
          // if you choose a value and you previously had unassigned selected, we remove the unassigned value
          nextSelections = selections
            .filter((selection) => selection.value !== UNASSIGNED_VALUE)
            .map((s) => s.value);
        } else if (
          selections.length > 1 &&
          selections.some((selection) => selection.value === UNASSIGNED_VALUE)
        ) {
          // if you choose the unassigned value, no other value can be selected
          nextSelections = [UNASSIGNED_VALUE];
        }
      }
      const selectedOptions = nextSelections.map((selection) =>
        flatOptions.find((option) => option.value === selection),
      );

      // store selected options incase they are not available after a search query
      setSelectedOptions(
        selectedOptions.filter(({ isSuggestion }) => !isSuggestion),
      );

      // pass back selected contacts
      onChange?.(
        selectedOptions.map((option) => ({
          shouldCreate: option.shouldCreate,
          ...option.account.data,
        })),
      );
      setSearchTerm('');
      if (textFeedback) {
        textFeedback('');
      }
    };

    const handleTextChange = (text, { action }) => {
      if (action === 'input-change') {
        setSearchTerm(text);

        if (textFeedback) {
          textFeedback(text);
        }
      }
    };

    const handleBlur = () => {
      setSearchTerm('');
    };

    const selectValue = value?.map((val) =>
      flatOptions.find((opt) => opt.value === val),
    );

    const commonProps = {
      closeMenuOnSelect: !isMulti,
      filterOption: null,
      isClearable: false,
      isMulti,
      inputValue: searchTerm,
      isLoading: isSearching,
      placeholder:
        placeholderProp ||
        (isMulti ? 'Select one or more accounts' : 'Select an account'),
      onChange: handleChange,
      onInputChange: handleTextChange,
      onBlur: handleBlur,
      options,
      value: selectValue,
      components: {
        Option: CustomOption,
        ...components,
      },
      ...restProps,
    };

    if (mode === 'create') {
      return (
        <>
          <CreatableSelect
            ref={ref}
            {...commonProps}
            placeholder={`${commonProps.placeholder} or enter an account name`}
            createLabelEntity="account"
            onCreateOption={accountDrawerDisclosure.onOpen}
            createOptionPosition="first"
            isValidNewOption={() => true}
          />
          <AccountDrawer
            {...accountDrawerDisclosure}
            mode={MODES.CREATE}
            callback={({ account }) => {
              const selections = convertToValues(selectValue)?.filter(
                (option) => option !== CREATE_NEW_ACCOUNT,
              );
              const nextSelections = [account, ...(selections ?? [])];
              receiveAccountRequest({ account });
              onChange?.(nextSelections);
            }}
            prefilledAccountName={value ? searchTerm : undefined}
          />
        </>
      );
    } else {
      return <Select ref={ref} {...commonProps} />;
    }
  },
);

AccountSelector.displayName = 'AccountSelector';
